import React, { useState, useEffect } from 'react';
import axios from 'axios';
import appConstants from '../constants/appConstants';
import Layout from '../components/Layout';
import { useNavigate } from 'react-router-dom';
import { Card, Button } from 'react-bootstrap';
import { useAppContext } from '../context/AppContext';

const getAuthToken = () => localStorage.getItem('authToken');

const BatchSelection = () => {
  const { selectedAcademicYear } = useAppContext();
  const [batches, setBatches] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchBatches();
  }, [selectedDate, selectedAcademicYear]);

  const formatDate = (date) => {
    const d = new Date(date);
    return d.toISOString().split('T')[0];
  };

  const getDayName = (date) => {
    const days = [
      'Sunday',
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
    ];
    return days[new Date(date).getDay()];
  };

  const handlePrevDay = () => {
    setSelectedDate((prev) => {
      const newDate = new Date(prev);
      newDate.setDate(newDate.getDate() - 1);
      return newDate;
    });
  };

  const handleNextDay = () => {
    setSelectedDate((prev) => {
      const newDate = new Date(prev);
      newDate.setDate(newDate.getDate() + 1);
      return newDate;
    });
  };

  const handleDateChange = (e) => {
    setSelectedDate(new Date(e.target.value));
  };

  const fetchBatches = async () => {
    const token = getAuthToken();
    if (!token) {
      alert('Unauthorized! Please log in.');
      return;
    }
    if (!selectedAcademicYear?.id) {
      console.warn('No academic year selected');
      setBatches([]);
      return;
    }
    setLoading(true);
    try {
      const response = await axios.get(
        `${appConstants.API_ROOT_URL}get-batches-v2?date=${formatDate(
          selectedDate,
        )}&academic_year_id=${selectedAcademicYear.id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setBatches(response.data.data);
    } catch (error) {
      console.error('Error fetching batches:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      {/* Header */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-3">
        <h5 className="mb-2 mb-md-0 fw-bold">Select a Batch for Attendance</h5>
        <div className="d-flex align-items-center gap-2">
          <span
            className="fw-semibold text-primary px-2"
            style={{
              fontSize: '0.95rem',
              letterSpacing: '0.3px',
            }}
          >
            {getDayName(selectedDate)}
          </span>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={handlePrevDay}
            className="d-flex align-items-center justify-content-center"
            style={{ width: '32px', height: '32px', padding: '0' }}
          >
            <i className="fas fa-chevron-left"></i>
          </Button>
          <input
            type="date"
            className="form-control form-control-sm w-auto text-center"
            value={formatDate(selectedDate)}
            onChange={handleDateChange}
          />
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={handleNextDay}
            className="d-flex align-items-center justify-content-center"
            style={{ width: '32px', height: '32px', padding: '0' }}
          >
            <i className="fas fa-chevron-right"></i>
          </Button>
        </div>
      </div>

      {/* Batches Grid */}
      <div className="position-relative">
        {/* Loading Overlay */}
        {loading && (
          <div
            className="position-absolute w-100 h-100 d-flex align-items-center justify-content-center"
            style={{
              background: 'rgba(255, 255, 255, 0.5)',
              zIndex: 10,
              borderRadius: '8px',
            }}
          >
            <div className="text-center">
              <div
                className="spinner-border text-primary mb-2"
                role="status"
                style={{ width: '3rem', height: '3rem' }}
              >
                <span className="visually-hidden">Loading...</span>
              </div>
              <div className="text-muted fw-semibold">Loading batches...</div>
            </div>
          </div>
        )}

        <div
          className="row"
          style={{
            opacity: loading ? 0.4 : 1,
            pointerEvents: loading ? 'none' : 'auto',
            transition: 'opacity 0.3s ease',
          }}
        >
          {batches.map((batch) => {
            const present = batch.summary?.present || 0;
            const absent = batch.summary?.absent || 0;
            const total = batch.summary?.total || 0;
            const attendancePercent =
              total > 0 ? Math.round((present / total) * 100) : 0;

            // Check if class was held (at least one student present)
            const hasAttendance = present > 0;

            return (
              <div key={batch.id} className="col-6 col-md-3 col-lg-2 mb-3">
                <Card
                  className="text-center p-2 shadow-sm rounded-3"
                  onClick={() => navigate(`/attendance/${batch.id}`)}
                  role="button"
                  style={{
                    background: hasAttendance
                      ? 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)'
                      : 'linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)',
                    color: '#333',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    border: hasAttendance
                      ? '2px solid #4caf50'
                      : '2px solid #e0e0e0',
                    boxShadow: hasAttendance
                      ? '0 4px 12px rgba(76, 175, 80, 0.25)'
                      : '0 2px 8px rgba(0, 0, 0, 0.08)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-3px)';
                    if (hasAttendance) {
                      e.currentTarget.style.boxShadow =
                        '0 6px 16px rgba(76, 175, 80, 0.35)';
                    } else {
                      e.currentTarget.style.boxShadow =
                        '0 4px 12px rgba(0, 0, 0, 0.12)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    if (hasAttendance) {
                      e.currentTarget.style.boxShadow =
                        '0 4px 12px rgba(76, 175, 80, 0.25)';
                    } else {
                      e.currentTarget.style.boxShadow =
                        '0 2px 8px rgba(0, 0, 0, 0.08)';
                    }
                  }}
                >
                  <Card.Body className="p-2">
                    <h6 className="fw-bold mb-1 text-primary">
                      {batch.class_name}
                    </h6>
                    <div className="text-muted small mb-2">
                      {batch.division}
                    </div>

                    {/* Compact Circle */}
                    <div
                      className="position-relative mx-auto mb-2"
                      style={{ width: '50px', height: '50px' }}
                    >
                      <svg width="50" height="50">
                        <circle
                          cx="25"
                          cy="25"
                          r="20"
                          stroke="#e0e0e0"
                          strokeWidth="4"
                          fill="none"
                        />
                        <circle
                          cx="25"
                          cy="25"
                          r="20"
                          stroke="#28a745"
                          strokeWidth="4"
                          fill="none"
                          strokeDasharray={`${attendancePercent * 1.25}, 125`}
                          strokeLinecap="round"
                          transform="rotate(-90 25 25)"
                        />
                      </svg>
                      <div
                        className="position-absolute top-50 start-50 translate-middle fw-bold"
                        style={{ fontSize: '0.8rem', color: '#333' }}
                      >
                        {attendancePercent}%
                      </div>
                    </div>

                    {/* Counts */}
                    <div className="d-flex justify-content-between small text-center">
                      <div className="flex-fill">
                        <div className="fw-bold text-success">{present}</div>
                      </div>
                      <div className="flex-fill">
                        <div className="fw-bold text-danger">{absent}</div>
                      </div>
                      <div className="flex-fill">
                        <div className="fw-bold text-dark">{total}</div>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </div>
            );
          })}
        </div>
      </div>
    </Layout>
  );
};

export default BatchSelection;
